import styles from './MoreActionButtonButton.module.scss';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  IconButton,
  ListItem,
  ListItemButton,
  Popover,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';

import { useUser } from '@work4all/data';

import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { canEditArticle } from '@work4all/utils/lib/permissions';

import { ArticleOverlayController } from '../../../../../../article/ArticleOverlayController';
import { IdArray } from '../edit-table/types';
import { useSelectedPositionState } from '../hooks/use-selected-position';

const CONVERTABLE_TO_INTERNAL_TYPES = [
  ErpPositionsKind.STANDARD,
  ErpPositionsKind.STUECKLISTE,
];

const CONVERTABLE_TO_NORMAL_TYPES = [
  ErpPositionsKind.INTERNE_POSITION,
  ErpPositionsKind.INTERNE_STUECKLISTE,
  ErpPositionsKind.TITEL_INTERN,
];

interface MoreActionButtonButtonProps {
  convertToInternal: (copyIds: IdArray) => void;
  convertToNormal: (copyIds: IdArray) => void;
  disabled: boolean;
  positions: Position[];
}

export const MoreActionButtonButton = (props: MoreActionButtonButtonProps) => {
  const { convertToInternal, convertToNormal, disabled, positions } = props;
  const { t } = useTranslation();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const { selectedPositionIds } = useSelectedPositionState();

  const { hideConvertingToInternal, hideConvertingToNormal } = useMemo(() => {
    const existingSelectedIds = selectedPositionIds.filter((x) =>
      positions.some((y) => y.localId === x)
    );
    if (existingSelectedIds.length > 1) {
      return {
        hideConvertingToInternal: true,
        hideConvertingToNormal: true,
      };
    }

    const positionsToConvert = positions.filter((x) => {
      const firstElement = existingSelectedIds?.[0];
      if (typeof firstElement === 'string') {
        const localArray = existingSelectedIds as string[];
        return localArray.includes(x.localId);
      }
      return true;
    });

    if (positionsToConvert.length === 0) {
      return {
        hideConvertingToInternal: true,
        hideConvertingToNormal: true,
      };
    }

    const hideConvertingToInternal = !positionsToConvert.some((x) =>
      CONVERTABLE_TO_INTERNAL_TYPES.includes(x.positionKind)
    );
    const hideConvertingToNormal = !positionsToConvert.some((x) =>
      CONVERTABLE_TO_NORMAL_TYPES.includes(x.positionKind)
    );

    return {
      hideConvertingToInternal,
      hideConvertingToNormal,
    };
  }, [positions, selectedPositionIds]);

  const dialogs = useDialogs();

  const articleId = useMemo(() => {
    const existingSelectedIds = selectedPositionIds.filter((x) =>
      positions.some((y) => y.localId === x)
    );
    return positions.find((x) => x.localId === existingSelectedIds?.[0])
      ?.articleId;
  }, [positions, selectedPositionIds]);

  const onEditArticle = useCallback(() => {
    setOpen(false);
    const handleAfterSave = (dialogId: string) => {
      dialogs.close(dialogId);
    };

    const dialog = dialogs.overlay(
      <ArticleOverlayController
        id={articleId}
        entity={Entities.article}
        onAfterSave={() => handleAfterSave(dialog.id)}
        onAfterQuickSave={() => handleAfterSave(dialog.id)}
      />
    );
  }, [articleId, dialogs]);

  const user = useUser();
  const enableArticleEdit = useMemo(
    () => canEditArticle(user, { id: articleId }),
    [user, articleId]
  );

  if (!(selectedPositionIds.length > 0)) return null;

  // Check if popover has any items to show
  const hasArticleActions =
    selectedPositionIds?.length === 1 && Boolean(articleId);
  const hasAnyItems =
    !hideConvertingToInternal || !hideConvertingToNormal || hasArticleActions;

  if (!hasAnyItems) return null;

  const handleConvertToInternal = () => {
    setOpen(false);
    convertToInternal(selectedPositionIds);
  };

  const handleConvertToNormal = () => {
    setOpen(false);
    convertToNormal(selectedPositionIds);
  };

  return (
    <React.Fragment>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={ref.current}
        classes={{ paper: styles.root }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ListItem className={styles.container}>
          {!hideConvertingToInternal && (
            <ListItemButton
              className={clsx(styles.color, styles.flex)}
              role={undefined}
              onClick={() => handleConvertToInternal()}
            >
              <Typography>{t('ERP.TRANSFORM.TO_INTERNAL')}</Typography>
            </ListItemButton>
          )}

          {!hideConvertingToNormal && (
            <ListItemButton
              className={clsx(styles.color, styles.flex)}
              role={undefined}
              onClick={handleConvertToNormal}
            >
              <Typography>{t('ERP.TRANSFORM.TO_NORMAL')}</Typography>
            </ListItemButton>
          )}

          {selectedPositionIds?.length === 1 && Boolean(articleId) && (
            <>
              <ListItemButton
                className={clsx(styles.color, styles.flex)}
                role={undefined}
                onClick={onEditArticle}
                disabled={!enableArticleEdit}
              >
                <Typography>{t('ERP.OPEN_ARTICLE_TO_EDIT')}</Typography>
              </ListItemButton>

              <ListItemButton
                className={clsx(styles.color, styles.flex)}
                role={undefined}
                disabled
              >
                <Typography>{t('ERP.TRANSFORM.TO_ARTICLE')}</Typography>
              </ListItemButton>
            </>
          )}
        </ListItem>
      </Popover>

      <IconButton
        ref={ref}
        color="primary"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <MoreHorizIcon />
      </IconButton>
    </React.Fragment>
  );
};
